import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  TrendingUp, 
  Sparkles, 
  AlertCircle, 
  Zap, 
  BarChart3, 
  ArrowRight,
  MousePointer2,
  DollarSign,
  RotateCcw,
  ShoppingCart,
  Percent,
  CheckCircle2
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip 
} from 'recharts';
import './Insights.css';

const Insights = () => {
  const revenueTrendData = [
    { name: 'Nov 10', value: 42000 },
    { name: 'Nov 12', value: 45000 },
    { name: 'Nov 14', value: 78000 },
    { name: 'Nov 16', value: 52000 },
    { name: 'Nov 18', value: 51000 },
    { name: 'Nov 20', value: 58000 },
    { name: 'Nov 22', value: 92000 },
    { name: 'Nov 24', value: 84000 },
  ];

  const neuralDrivers = [
    { label: 'Fewer clicks', val: '12.4K', color: 'orange', progress: 45, icon: MousePointer2 },
    { label: 'Pricing concern', val: '8.2K', color: 'blue', progress: 92, icon: DollarSign },
    { label: 'no repeat buyer', val: '15.6K', color: 'purple', progress: 60, icon: RotateCcw },
    { label: 'add to cart', val: '42.1K', color: 'cyan', progress: 78, icon: ShoppingCart },
    { label: 'discount conversion', val: '28.9K', color: 'pink', progress: 35, icon: Percent },
    { label: 'Purchase', val: '5.2K', color: 'emerald', progress: 85, icon: CheckCircle2 },
  ];

  const diagnosticInsights = [
    {
      title: 'Revenue Surge Pattern',
      description: 'Organic DM volume increased by 42% following the 8PM IG Story post. Most conversions originated from the \'Ready to Buy\' segment.',
      type: 'positive',
      icon: TrendingUp
    },
    {
      title: 'Engagement Decay',
      description: 'Average response time on DMs slowed down by 14 minutes today. AI predicts a potential 8.5% conversion drop if not addressed.',
      type: 'negative',
      icon: AlertCircle
    },
    {
      title: 'Offer Performance',
      description: 'The \'Free Shipping\' trigger is significantly outperforming \'10% Off\' for customers with cart values exceeding $120.',
      type: 'neutral',
      icon: Zap
    }
  ];

  const recommendations = [
    {
      title: 'Scale Story Automation for campaign',
      impact: 'High',
      duration: '5 min',
      action: 'Deploy Now'
    },
    {
      title: 'Switch High-AOV Segments to \'Free Shipping\' Offer',
      impact: 'Medium',
      duration: '2 min',
      action: 'Apply Strategy'
    },
    {
      title: 'Enable AI Smart-Responder to handle DM Surge',
      impact: 'Critical',
      duration: '10 min',
      action: 'Initialize AI'
    }
  ];

  return (
    <>
      <Helmet>
        <title>AI Insights - Revexia AI Revenue Brain</title>
        <meta name="description" content="Real-time AI insights and strategic recommendations" />
      </Helmet>

      <div className="insights-page-wrapper">
        <div className="container-fluid px-4">
          {/* Top Section with Split Panel */}
          <div className="row g-4 mb-5">
            {/* Left: Neural Driver Metrics */}
            <div className="col-12 col-lg-4">
              <div className="neural-drivers-card">
                <div className="neural-drivers-header">
                  <div className="neural-header-content">
                    <div className="neural-icon-wrapper">
                      <Sparkles size={24} />
                    </div>
                    <h3 className="neural-title">Revenue Insights</h3>
                  </div>
                  <p className="neural-description">
                    Real-time weight volume assigned to cross-channel behavioral signals.
                  </p>
                </div>

                <div className="neural-drivers-list">
                  {neuralDrivers.map((driver, i) => (
                    <div key={i} className="neural-driver-item">
                      <div className="neural-driver-header">
                        <div className="neural-driver-left">
                          <div className="neural-driver-icon">
                            <driver.icon size={14} />
                          </div>
                          <span className="neural-driver-label">{driver.label}</span>
                        </div>
                        <span className="neural-driver-value">{driver.val}</span>
                      </div>
                      <div className="neural-driver-progress-track">
                        <div 
                          className={`neural-driver-progress-bar progress-${driver.color}`}
                          style={{ width: `${driver.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="explainable-ai-card">
                  <p className="explainable-ai-label">Explainable AI Node</p>
                  <p className="explainable-ai-text">
                    "Purchase signals are concentrating within 48h of initial cart activity, indicating a high-sensitivity conversion window."
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Revenue Anomalies Chart */}
            <div className="col-12 col-lg-8">
              <div className="revenue-anomalies-section">
                <div className="anomalies-header">
                  <div className="anomalies-header-left">
                    <BarChart3 size={20} className="anomalies-icon" />
                    <h3 className="anomalies-title">Revenue Anomalies</h3>
                  </div>
                  <div className="anomalies-legend">
                    <div className="legend-item">
                      <span className="legend-dot blue"></span>
                      <span className="legend-text">Predicted</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot cyan"></span>
                      <span className="legend-text">Realized</span>
                    </div>
                  </div>
                </div>
                <div className="anomalies-chart-card">
                  <div className="chart-bg-glow"></div>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={revenueTrendData}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22D3EE" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#22D3EE" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                        <XAxis 
                          dataKey="name" 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} 
                          dy={10}
                        />
                        <YAxis 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} 
                          tickFormatter={(val) => `$${val/1000}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(13, 15, 41, 0.95)', 
                            border: '1px solid rgba(255,255,255,0.1)', 
                            borderRadius: '12px',
                            backdropFilter: 'blur(4px)'
                          }}
                          itemStyle={{ color: '#fff' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#22D3EE" 
                          strokeWidth={3} 
                          fillOpacity={1} 
                          fill="url(#colorRevenue)" 
                          animationDuration={2000}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Diagnostic Insights Section */}
          <section className="diagnostic-insights-section">
            <div className="section-header-with-icon">
              <Sparkles size={20} className="section-icon purple" />
              <h3 className="section-main-title">Diagnostic Insights</h3>
            </div>
            <div className="row g-4">
              {diagnosticInsights.map((insight, index) => (
                <div key={index} className="col-12 col-md-4">
                  <div className={`insight-card insight-${insight.type}`}>
                    <div className={`insight-glow ${insight.type}`}></div>
                    <div className="insight-content">
                      <div className={`insight-icon-wrapper ${insight.type}`}>
                        <insight.icon size={20} />
                      </div>
                      <div className="insight-text">
                        <h4 className="insight-title">{insight.title}</h4>
                        <p className="insight-description">{insight.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Strategic Recommendations Section */}
          <section className="recommendations-section">
            <div className="section-header-with-icon">
              <AlertCircle size={20} className="section-icon orange" />
              <h3 className="section-main-title">Strategic Recommendations</h3>
            </div>
            <div className="recommendations-list">
              {recommendations.map((rec, index) => (
                <div key={index} className="recommendation-card">
                  <div className="recommendation-left">
                    <div className="recommendation-icon-wrapper">
                      <Zap size={22} />
                    </div>
                    <div className="recommendation-info">
                      <h5 className="recommendation-title">{rec.title}</h5>
                      <div className="recommendation-meta">
                        <span className={`recommendation-impact impact-${rec.impact.toLowerCase()}`}>
                          <TrendingUp size={12} /> {rec.impact} Impact
                        </span>
                        <span className="recommendation-divider">•</span>
                        <span className="recommendation-duration">Est. {rec.duration} to implement</span>
                      </div>
                    </div>
                  </div>
                  <button className="recommendation-action-btn">
                    <span>{rec.action}</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Insights;
